// @Version: 1.0.4
import { Component, EventEmitter, Output, Input, OnDestroy, forwardRef, signal, OnChanges, SimpleChanges, inject } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CustomValidatorsToggle } from './custom-validators';
import { FC_TOGGLE } from './toggle.model';
import { IFieldItem } from '../field-item.model';
import { FieldItemToggleService } from './toggle.service';
import { ErrorMessagesService } from 'src/app/shared/services/errorMessages/error-messages.service';
import { TRANSLATE_PATH as TRANSLATE_PATH_SHARED } from 'src/app/shared/shared.model';
import { filter, map, Subscription } from 'rxjs';

@UntilDestroy()
@Component({
   selector: 'app-field-item-toggle',
   templateUrl: './toggle.component.html',
   providers: [
      {
         provide: NG_VALUE_ACCESSOR,
         useExisting: forwardRef(() => FieldItemToggleComponent),
         multi: true
      },
      {
         provide: NG_VALIDATORS,
         useExisting: forwardRef(() => FieldItemToggleComponent),
         multi: true
      },
      FieldItemToggleService,
      ErrorMessagesService
   ],
   standalone: false
})

export class FieldItemToggleComponent implements OnDestroy, ControlValueAccessor, Validators, OnChanges  {
   public TRANSLATE_PATH_SHARED: typeof TRANSLATE_PATH_SHARED = TRANSLATE_PATH_SHARED
   public FC_TOGGLE: typeof FC_TOGGLE = FC_TOGGLE;

   private fb_: FormBuilder = inject(FormBuilder);
   public done$$ = signal(false);
   public fgToggle: FormGroup = this.fb_.group({});   
   private fieldItem_: IFieldItem = {} as IFieldItem;
   private sub_?: Subscription;

   @Input() clearValueIfDisabled: boolean = true;
     
   @Input()
   set fieldItem (value: IFieldItem) {
      this.fieldItem_ = value || {};
   }
   get fieldItem (): IFieldItem {
      return this.fieldItem_;
   }

   @Output() isRequiredMarker: EventEmitter<boolean> = new EventEmitter<boolean>();

   get value (): string {
      return this.fgToggle.get(FC_TOGGLE.TOGGLEBUTTON)?.value ?? '';
   }

   constructor (public sFieldItemToggle: FieldItemToggleService, private vToggle: CustomValidatorsToggle) { }
  

   ngOnChanges(changes: SimpleChanges): void {
      if (changes && changes.fieldItem &&  (JSON.stringify(changes.fieldItem.currentValue) != JSON.stringify(changes.fieldItem.previousValue))) {
         this.init(changes.fieldItem.currentValue);
         this.onValidationChange();
         this.done$$.set(true);
      }
   }

   ngOnDestroy (): void {
      this.reset();
   }

   validate (control: AbstractControl): ValidationErrors | null {
      this.fgToggle.updateValueAndValidity( {emitEvent: true, onlySelf: true });
      return this.fgToggle.valid ? null : this.fgToggle.errors;
   }

   writeValue (value: string | undefined | null): void {
      this.fgToggle.get(FC_TOGGLE.TOGGLEBUTTON)?.setValue(value ? value : undefined);
      if (typeof value == 'string' && value.trim().length > 0) this.setDirtyTouched();
      this.onValidationChange();
   }
   private setDirtyTouched (): void {
      this.fgToggle.get(FC_TOGGLE.TOGGLEBUTTON)?.markAsDirty(); 
      this.fgToggle.get(FC_TOGGLE.TOGGLEBUTTON)?.markAsTouched();
   }

   registerOnChange (fn: any): void {
      this.onChange = fn
   }

   registerOnTouched (fn: any): void {
      this.onTouched = fn
   }

   private onTouched () { }
   private onChange (_: string) { }

   setDisabledState (isDisabled: boolean | undefined): void { 
      if (isDisabled) {
         this.fgToggle.disable();
         if (this.clearValueIfDisabled) this.fgToggle.get(FC_TOGGLE.TOGGLEBUTTON)?.setValue("");
         this.fgToggle.markAsDirty();
      }
      else this.fgToggle.enable();
      this.onValidationChange();
   }

   onValidationChange: any = () => {};

   registerOnValidatorChange? (fn: () => void): void {
      this.onValidationChange = fn;
   }

   private init (fieldItem: IFieldItem): void {
      this.sFieldItemToggle.transformAttribute(fieldItem);
      this.initFormGroup(fieldItem);
      this.done$$.set(true);
      this.onValidationChange();
   }

   private reset (): void {
      this.done$$.set(false);
      this.fgToggle = this.fb_.group({});
      this.fieldItem_ = {} as IFieldItem;
      this.isRequiredMarker.emit(false); 
      this.sFieldItemToggle.reset();
   }

   private initFormGroup (fieldItem: IFieldItem): boolean {
      if (this.sub_) {
         this.sub_.unsubscribe();
         this.sub_ = undefined;
      }
      this.fgToggle.addControl(FC_TOGGLE.TOGGLEBUTTON, this.fb_.control(fieldItem.content));
      if (fieldItem.valid) this.fgToggle.addValidators(this.vToggle.isValid(this.fgToggle, fieldItem));
      if (fieldItem.required) this.fgToggle.addValidators(this.vToggle.isRequired(this.fgToggle));

      this.sub_ = this.fgToggle.get(FC_TOGGLE.TOGGLEBUTTON)?.valueChanges
      .subscribe(_ => {
         if (!this.value && this.fgToggle.get(FC_TOGGLE.TOGGLEBUTTON)?.pristine) return;
         this.onChange(this.value);
         this.onValidationChange();
         this.onTouched();
      })

      this.isRequiredMarker.emit(fieldItem.required);
      return true;
   }
}